import { useParams, Navigate, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { AffiliateButton, ScoreBadge } from "../components/Affiliate";
import { VpnLogo } from "../components/VpnLogo";
import { RelatedGuides } from "../components/RelatedGuides";
import { CtaBanner } from "../components/CtaBanner";
import { getVpn } from "../data/vpns";
import { getBestFor, BEST_FOR } from "../data/bestFor";
import { Trophy } from "lucide-react";

const BestForPage = () => {
  const { slug } = useParams();
  const page = getBestFor(slug);
  if (!page) return <Navigate to="/best-vpn-for" replace />;

  return (
    <Layout>
      <Seo title={page.metaTitle} description={page.metaDescription} />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">Ranked for your use case</p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[page.title]} />
          </h1>
          {page.intro.map((t, i) => (
            <p key={i} className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">{t}</p>
          ))}
          <div className="mt-8">
            <AffiliateButton href={getVpn(page.order[0]).link} testId="bestfor-hero-cta">
              Get {getVpn(page.order[0]).name} — Top Pick for {page.label}
            </AffiliateButton>
          </div>
        </div>
      </section>

      <Breadcrumbs crumbs={[{ label: "Best VPN For", to: "/best-vpn-for" }, { label: page.label }]} />

      <section className="mx-auto max-w-5xl space-y-6 px-6 py-16 lg:px-8" data-testid="bestfor-ranked-list">
        {page.order.map((s, i) => {
          const v = getVpn(s);
          const top = i === 0;
          return (
            <Reveal key={s} delay={i * 0.05}>
              <div
                data-testid={`bestfor-card-${s}`}
                className={`relative flex flex-col gap-6 border p-8 sm:flex-row sm:items-start ${
                  top ? "border-[#4687FF] bg-[#4687FF]/[0.04] shadow-[0_0_0_1px_#4687FF]" : "border-slate-200 bg-white"
                }`}
              >
                {top && (
                  <div className="absolute -top-3.5 left-8 flex items-center gap-1.5 bg-[#4687FF] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
                    <Trophy size={12} /> Best for {page.label}
                  </div>
                )}
                <div className="flex items-center gap-5">
                  <span className="font-display text-4xl font-black tracking-tighter text-slate-200">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <VpnLogo vpn={v} size={72} />
                  <ScoreBadge score={v.score} />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">{v.name}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{page.notes[s]}</p>
                  <Link to={`/reviews/${s}`} className="mt-2 inline-block text-xs font-semibold text-[#4687FF] underline underline-offset-4">
                    Read full {v.name} review →
                  </Link>
                </div>
                <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                  <span className="font-display text-sm text-slate-500">
                    from <span className="text-xl font-extrabold text-[#0F172A]">{v.price}</span>/mo
                  </span>
                  <AffiliateButton href={v.link} testId={`bestfor-cta-${s}`} variant={top ? "primary" : "dark"}>
                    {v.cta}
                  </AffiliateButton>
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20 lg:px-8">
        <Reveal className="border-t border-slate-200 pt-10">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-400">More Use Cases</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {BEST_FOR.filter((b) => b.slug !== slug).map((b) => (
              <Link
                key={b.slug}
                to={`/best-vpn-for/${b.slug}`}
                className="border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
              >
                {b.label}
              </Link>
            ))}
          </div>
        </Reveal>

        <RelatedGuides vpn={getVpn(page.order[0])} excludeBestFor={slug} />
      </section>
      <CtaBanner />
    </Layout>
  );
};

export default BestForPage;
